// src/dialogs/PurchasesDialog.jsx
import React, { useMemo } from "react";
import {
  Dialog,
  Box,
  Typography,
  Stack,
  Divider,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
} from "@mui/material";

import StoreIcon from "@mui/icons-material/Store";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { formatDate } from "../../../utils/helpers";

/* ─────────────────────────────────────────
   Keyframes
───────────────────────────────────────── */
const ANIM = `
  @keyframes sheetUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes cardIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

/* ─────────────────────────────────────────
   Warranty status badge
───────────────────────────────────────── */
function WarrantyBadge({ expiryDate }) {
  if (!expiryDate) return null;
  const isActive = new Date(expiryDate) > new Date();
  return (
    <Chip
      icon={
        isActive ? (
          <VerifiedIcon sx={{ fontSize: "13px !important", color: "#16a34a !important" }} />
        ) : (
          <ErrorOutlineIcon sx={{ fontSize: "13px !important", color: "#dc2626 !important" }} />
        )
      }
      label={isActive ? "Active" : "Expired"}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.3,
        flexShrink: 0,
        bgcolor: isActive ? "#f0fdf4" : "#fef2f2",
        color: isActive ? "#15803d" : "#dc2626",
        border: `1px solid ${isActive ? "#bbf7d0" : "#fecaca"}`,
        borderRadius: 0.2,
        "& .MuiChip-icon": { ml: "5px" },
      }}
    />
  );
}

/* ─────────────────────────────────────────
   Single purchase card
───────────────────────────────────────── */
function PurchaseCard({ p, isActive, retailerName, retailerAddress, index }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 0.2,
        border: "1px solid",
        borderColor: isActive ? "#16a34a" : "#e8f0fe",
        bgcolor: isActive ? "#f0fdf4" : "#fff",
        boxShadow: isActive
          ? "0 2px 12px rgba(22,163,74,0.14)"
          : "0 1px 4px rgba(0,0,0,0.05)",
        animation: "cardIn 0.3s ease both",
        animationDelay: `${index * 0.06}s`,
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
      }}
    >
      <CardContent
        sx={{
          p: { xs: "12px !important", sm: "14px !important" },
        }}
      >

        {/* ── Row 1: Avatar + model name + warranty badge ── */}
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Avatar
            sx={{
              width: { xs: 34, sm: 38 },
              height: { xs: 34, sm: 38 },
              borderRadius: 0.2,
              bgcolor: isActive ? "#dcfce7" : "#eff6ff",
              border: `1px solid ${isActive ? "#bbf7d0" : "#dbeafe"}`,
              flexShrink: 0,
            }}
          >
            <StoreIcon
              sx={{
                fontSize: { xs: 16, sm: 18 },
                color: isActive ? "#16a34a" : "#2563eb",
              }}
            />
          </Avatar>

          {/* Name + retailer — must have minWidth:0 to allow ellipsis */}
          <Box flex={1} minWidth={0}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={0.8}
            >
              <Typography
                sx={{
                  fontSize: { xs: 12, sm: 13 },
                  fontWeight: 700,
                  color: "#111827",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {p.model_name}
              </Typography>
              <WarrantyBadge expiryDate={p.warranty_expiry} />
            </Stack>

            {retailerName && (
              <Typography
                sx={{
                  fontSize: { xs: 10, sm: 11 },
                  color: "#64748b",
                  mt: 0.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {retailerName}
              </Typography>
            )}
          </Box>
        </Stack>

        <Divider sx={{ my: { xs: 1, sm: 1.2 }, borderColor: "#f1f5f9" }} />

        {/* ── Row 2: Meta info grid ── */}
        <Stack spacing={0.9}>

          {/* Purchase date + order — two columns */}
          <Stack direction="row" spacing={{ xs: 1, sm: 2 }}>
            <Stack direction="row" spacing={0.6} alignItems="center" flex={1} minWidth={0}>
              <EventIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
              <Box minWidth={0}>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "#94a3b8",
                    fontWeight: 600,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                  }}
                >
                  Purchased
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 10, sm: 11 },
                    fontWeight: 600,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatDate(p.purchase_date)}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.6} alignItems="center" flex={1} minWidth={0}>
              <ReceiptLongIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
              <Box minWidth={0}>
                <Typography
                  sx={{
                    fontSize: 9,
                    color: "#94a3b8",
                    fontWeight: 600,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                  }}
                >
                  Order
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 10, sm: 11 },
                    fontWeight: 600,
                    color: "#374151",
                    fontFamily: "monospace",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.order_no}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          {/* Warranty expiry */}
          {p.warranty_expiry && (
            <Stack direction="row" spacing={0.6} alignItems="center">
              <VerifiedIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: { xs: 10, sm: 11 },
                  color: "#475569",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Warranty till{" "}
                <Box component="span" sx={{ fontWeight: 700, color: "#111827" }}>
                  {formatDate(p.warranty_expiry)}
                </Box>
              </Typography>
            </Stack>
          )}

          {/* IMEI + Serial pill */}
          {(p.imei || p.serial_no) && (
            <Stack
              direction="row"
              sx={{
                bgcolor: "#f8fafc",
                border: "1px solid #f1f5f9",
                borderRadius: 0.2,
                overflow: "hidden",
              }}
            >
              {/* IMEI */}
              {p.imei && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  flex={1}
                  sx={{ px: { xs: 1, sm: 1.2 }, py: 0.9 }}
                >
                  <FingerprintIcon
                    sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }}
                  />
                  <Box minWidth={0}>
                    <Typography
                      sx={{
                        fontSize: 9,
                        color: "#94a3b8",
                        fontWeight: 600,
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                      }}
                    >
                      IMEI
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 9, sm: 10 },
                        fontWeight: 600,
                        color: "#374151",
                        fontFamily: "monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.imei}
                    </Typography>
                  </Box>
                </Stack>
              )}

              {/* Serial — conditionally shown */}
              {p.imei && p.serial_no && p.serial_no !== "—" && (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: "#e2e8f0" }}
                />
              )}

              {p.serial_no && p.serial_no !== "—" && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  flex={1}
                  sx={{ px: { xs: 1, sm: 1.2 }, py: 0.9 }}
                >
                  <QrCodeIcon
                    sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }}
                  />
                  <Box minWidth={0}>
                    <Typography
                      sx={{
                        fontSize: 9,
                        color: "#94a3b8",
                        fontWeight: 600,
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                      }}
                    >
                      Serial
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 9, sm: 10 },
                        fontWeight: 600,
                        color: "#374151",
                        fontFamily: "monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {p.serial_no}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Stack>
          )}

          {/* Retailer address */}
          {retailerAddress && (
            <Stack direction="row" spacing={0.6} alignItems="flex-start" mt={0.2}>
              <LocationOnIcon
                sx={{ fontSize: 13, color: "#94a3b8", mt: "1px", flexShrink: 0 }}
              />
              <Typography
                sx={{
                  fontSize: { xs: 9, sm: 10 },
                  color: "#94a3b8",
                  lineHeight: 1.5,
                  // Allow address to wrap naturally — don't truncate
                  wordBreak: "break-word",
                }}
              >
                {retailerAddress}
              </Typography>
            </Stack>
          )}

        </Stack>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────────────────────
   Main Dialog
───────────────────────────────────────── */
export default function PurchasesDialog({
  open,
  onClose,
  purchases = [],
  retailerInfo,
  activePurchaseId = null,
}) {
  console.log("retailer info: ", retailerInfo);

  /* ── Business logic — UNCHANGED ── */
  const adaptedPurchases = useMemo(() => {
    return purchases.map((d) => ({
      id: d.id,
      model_name: d.product_name || "Device",
      purchase_date: d.created_at || null,
      order_no: d.w_code || "—",
      serial_no: d.serial || "—",
      imei: d.imei1 || d.imei2 || "",
      warranty_expiry: d.expiry_date || null,
    }));
  }, [purchases]);

  const sortedPurchases = [...adaptedPurchases].sort((a, b) => {
    const da = new Date(a.purchase_date || 0).getTime();
    const db = new Date(b.purchase_date || 0).getTime();
    return db - da;
  });

  const uniqueShopCount = new Set(
    adaptedPurchases.map((p) => p?.purchased_from?.name?.toLowerCase())
  ).size;

  const retailerName = useMemo(() => {
    if (!retailerInfo) return "";
    return retailerInfo.business_name || "";
  }, [retailerInfo]);

  const retailerAddress = useMemo(() => {
    if (!retailerInfo) return "";
    return [
      retailerInfo.address_line1,
      retailerInfo.address_line2,
      retailerInfo.city || retailerInfo.district,
      retailerInfo.state,
      retailerInfo.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  }, [retailerInfo]);

  console.log(" address: ", retailerAddress);

  const activeCount = adaptedPurchases.filter(
    (p) => p.warranty_expiry && new Date(p.warranty_expiry) > new Date()
  ).length;

  return (
    <>
      <style>{ANIM}</style>

      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        // keepMounted for smoother animation
        keepMounted={false}
        sx={{
          "& .MuiDialog-paper": {
            margin: 0,
            width: "100%",
            maxWidth: "100%",
            borderRadius: "14px 14px 0 0",
            position: "fixed",
            bottom: 0,
            // Respect dynamic viewport height (mobile browsers with toolbar)
            maxHeight: { xs: "90svh", sm: "88vh" },
            boxShadow: "0 -8px 40px rgba(0,0,0,0.14)",
            animation: "sheetUp 0.28s cubic-bezier(0.32,0.72,0,1) both",
            overflow: "hidden",
            // Prevent content from hiding under the notch on landscape
            paddingLeft: "env(safe-area-inset-left)",
            paddingRight: "env(safe-area-inset-right)",
          },
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(3px)",
            bgcolor: "rgba(0,0,0,0.35)",
          },
        }}
      >

        {/* ── Drag handle ── */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0 }}>
          <Box
            sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: "#e2e8f0" }}
          />
        </Box>

        {/* ── Sticky header ── */}
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            pt: 1.5,
            pb: 1.2,
            borderBottom: "1px solid #f1f5f9",
            bgcolor: "#fff",
            // Stick header while list scrolls
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">

            <Stack direction="row" spacing={1.2} alignItems="center" flex={1} minWidth={0} mr={1}>
              <Box
                sx={{
                  width: { xs: 32, sm: 36 },
                  height: { xs: 32, sm: 36 },
                  borderRadius: 0.2,
                  bgcolor: "#f0fdf4",
                  border: "1px solid #dcfce7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <StoreIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: "#16a34a" }} />
              </Box>

              <Box minWidth={0}>
                <Typography
                  sx={{
                    fontSize: { xs: 14, sm: 15 },
                    fontWeight: 800,
                    color: "#111827",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  My Purchases
                </Typography>
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: "#64748b" }}>
                    {adaptedPurchases.length} device{adaptedPurchases.length !== 1 ? "s" : ""}
                  </Typography>
                  {activeCount > 0 && (
                    <>
                      <Box
                        sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: "#cbd5e1", flexShrink: 0 }}
                      />
                      <Typography
                        sx={{ fontSize: { xs: 10, sm: 11 }, color: "#16a34a", fontWeight: 600 }}
                      >
                        {activeCount} active
                      </Typography>
                    </>
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Close — touch target 44×44 */}
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 0.2,
                width: 36,
                height: 36,
                flexShrink: 0,
                // Extra invisible tap area
                p: 0.5,
                "&:hover": { bgcolor: "#f1f5f9" },
              }}
            >
              <CloseIcon fontSize="small" sx={{ color: "#475569" }} />
            </IconButton>
          </Stack>

          {/* Retailer banner */}
          {retailerName && (
            <Stack
              direction="row"
              spacing={0.8}
              alignItems="flex-start"
              mt={1.4}
              sx={{
                bgcolor: "#f8fafc",
                border: "1px solid #e8f0fe",
                borderRadius: 0.2,
                px: { xs: 1.2, sm: 1.4 },
                py: 1,
              }}
            >
              <LocationOnIcon
                sx={{ fontSize: 14, color: "#64748b", mt: "1px", flexShrink: 0 }}
              />
              <Box minWidth={0}>
                <Typography
                  sx={{
                    fontSize: { xs: 10, sm: 11 },
                    fontWeight: 700,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {retailerName}
                </Typography>
                {retailerAddress && (
                  <Typography
                    sx={{
                      fontSize: { xs: 9, sm: 10 },
                      color: "#94a3b8",
                      lineHeight: 1.5,
                      mt: 0.2,
                      // Allow address to wrap
                      wordBreak: "break-word",
                    }}
                  >
                    {retailerAddress}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </Box>

        {/* ── Scrollable list ── */}
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            pt: 1.5,
            // iOS safe area at bottom (notch phones)
            pb: "max(24px, env(safe-area-inset-bottom, 24px))",
            overflowY: "auto",
            overflowX: "hidden",
            flexGrow: 1,
            bgcolor: "#f8fafc",
            // Custom slim scrollbar
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#e2e8f0",
              borderRadius: 99,
            },
            // Smooth momentum scrolling on iOS
            WebkitOverflowScrolling: "touch",
          }}
        >
          {sortedPurchases.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" spacing={1} py={5}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 0.2,
                  bgcolor: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <StoreIcon sx={{ color: "#94a3b8", fontSize: 24 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>
                No purchases found
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={{ xs: 1.2, sm: 1.4 }}>
              {sortedPurchases.map((p, index) => (
                <PurchaseCard
                  key={p.id}
                  p={p}
                  index={index}
                  isActive={p.id === activePurchaseId}
                  retailerName={retailerName}
                  retailerAddress={retailerAddress}
                />
              ))}
            </Stack>
          )}
        </Box>

      </Dialog>
    </>
  );
}